/**
 * Blast-radius disclosure: the ONE place the words for a destructive confirm
 * come from, on every surface (dashboard, extension, team review). Surfaces
 * fetch a DeletionImpact from POST /api/deletion-impact and render this
 * builder's output; none of them composes its own sentence, so the three
 * cannot drift. Spec: docs/superpowers/specs/2026-09-16-blast-radius-disclosure-design.md
 */
import { EXPORT_PROVIDERS, getExportProviderLabel } from './api/types.js';
/**
 * The kinds of delete this builder can describe, as a RUNTIME tuple so the
 * union type, the seam's accept-set (`KNOWN_KINDS` below) and the guards that
 * must hold for every kind all read ONE list. Same shape as `EXPORT_PROVIDERS`
 * in api/types.ts, and for the same reason: a hand-typed second copy of a list
 * is how the two drift. Adding a fifth kind here fails `typecheck` at
 * `describeDeletionImpact`'s `default` branch until it gets a case.
 */
export const IMPACT_KINDS = ['issues', 'projects', 'clientReview', 'account'];
/**
 * The phrase a user types to confirm deleting their account. ONE definition:
 * the dashboard's typed gate and second gate, this builder's `typedPhrase`, and
 * the server's request validator (routes/account-delete.ts) all read it. They
 * were three separately typed strings, and any one drifting makes account
 * deletion silently impossible: the button accepts a phrase the second gate or
 * the server then refuses.
 */
export const ACCOUNT_DELETE_CONFIRMATION_PHRASE = 'Delete account';
/**
 * The placeholder every surface shows while its `POST /api/deletion-impact`
 * request is in flight, hand-typed identically in the extension's
 * `DeletionImpactNotice` and the dashboard's `DeletionConfirmDialog` until
 * fix round 1 (controller ruling R16) moved it here so the two cannot drift.
 * Not part of `describeDeletionImpact`'s return: that function only runs
 * once a `DeletionImpact` has actually arrived, so the counting placeholder
 * has to live outside it.
 */
export const DELETION_IMPACT_COUNTING_TEXT = 'Counting what this removes...';
/**
 * The danger button's label, derived from what the CALLER already knows.
 *
 * Split out of `describeDeletionImpact` on 2026-09-17 (R29) because nothing in
 * it needs the counted impact: it reads `kind` and `targetCount`, and
 * `targetCount` is the user's own selection, known at the instant the confirm
 * opens. Surfaces that rendered `copy?.confirmLabel ?? 'Delete'` therefore let
 * the button CHANGE SIZE when counting resolved, and the two dropdown confirms
 * hang in a right-anchored (`right: 0`) shrink-to-fit menu, so the menu grew
 * ~23px leftward and the live DELETE button slid onto ground CANCEL had held.
 * Measured off two frames of one harness run
 * (docs/test-reports/2026-09-17-deletion-disclosure-review-mini/fixed/,
 * `04-counting.png` -> `02-bulk-confirm.png`).
 *
 * `describeDeletionImpact` returns exactly this, so the label a confirm shows
 * while counting and the label it shows once ready are the same string by
 * construction and cannot drift into a second silent resize.
 *
 * Total over unknown kinds for the same reason the builder is: it runs inside
 * render on every surface.
 */
export function deletionConfirmLabel(kind, count) {
    switch (kind) {
        case 'issues': return count === 1 ? 'Delete issue' : `Delete ${count} issues`;
        case 'projects': return count === 1 ? 'Delete project' : `Delete ${count} projects`;
        case 'clientReview': return 'Delete link';
        case 'account': return 'Delete my account';
        default: return 'Delete';
    }
}
/**
 * `exportProviders` reaches here from `tickets.exported_to`, a jsonb column, so
 * the strings are whatever was written into it and not an `ExportProvider`.
 * Resolved against the ONE list rather than a second hand-typed map: api/types.ts
 * already owns `EXPORT_PROVIDERS` + `getExportProviderLabel` under a docstring
 * promising a single list, and the copy of it that used to sit here had drifted
 * far enough to carry a `slack` key, which is not an export destination at all
 * (Slack is a notification target; the same docstring says so).
 *
 * An unrecognised slug is NOT rendered: "2 are already in github and stay there"
 * puts a raw lowercase slug inside destructive copy, reading like a product name
 * we endorse and can vouch for. It degrades to the same "other tools" wording
 * that two or more providers already use, which is true of any slug we cannot
 * name.
 */
const isExportProvider = (p) => EXPORT_PROVIDERS.includes(p);
/**
 * The permanence warning, in the two shapes the lists under it need. Undo and
 * soft-delete are not shipped, so every confirm carries one of these (R33):
 * before fix round 2 the `issues` and `clientReview` cases returned
 * `lead: null` and said nothing about permanence anywhere, dropping the
 * "This cannot be undone." that the dashboard's own copy had carried.
 *
 * LEAD_DELETES ends in a colon, so it PROMISES a list under it and may only be
 * used when there is one (see `leadOverLines`).
 */
const LEAD_DELETES = 'This permanently deletes:';
const LEAD_UNDONE = 'This cannot be undone.';
/**
 * A lead ending in a colon with nothing under it is a dangling promise: the
 * confirm renders "This permanently deletes:" and then no list, which reads as
 * a failure of the product rather than as an empty target. Executed against the
 * pre-fix builder, an empty project in a solo team produced exactly that
 * (`lead: 'This permanently deletes:'  lines: []  keeps: []`), replacing the
 * honest "Delete this project and all its issues? This cannot be undone." it
 * had before the preview existed.
 *
 * `lines` can be empty on any kind, not just projects: every line builder reads
 * its counts out of `deletes`, and only team review validates its seam, so a
 * response with `deletes: []` reaches render on the other two. This keeps the
 * stakes and drops the promise.
 */
const leadOverLines = (lines) => (lines.length > 0 ? LEAD_DELETES : LEAD_UNDONE);
/**
 * Total over both shapes an unvalidated body can carry here, because this
 * builder runs INSIDE render on every surface and a throw unmounts the confirm
 * dialog at the moment the user pressed delete.
 *
 * - `Array.isArray`: the extension and dashboard seams cast the response body,
 *   so a `deletes`/`keeps` the server omitted arrives as `undefined`.
 * - `l?.key`: the NULL ELEMENT case, which no seam guard can stop. Team review
 *   does validate (review-app/lib/deletionImpact.ts) and `Array.isArray([null])`
 *   is true, so `deletes: [null]` passes that guard and threw here. Measured:
 *   `TypeError: Cannot read properties of null (reading 'key')`. A validated
 *   seam is defence in depth; totality has to live in the builder.
 *
 * Same reasoning as the `default` branch below; see `describeDeletionImpact`.
 */
const n = (lines, key) => (Array.isArray(lines) ? lines : []).find((l) => l?.key === key)?.count ?? 0;
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
function exportedLine(count, providers) {
    if (count === 0)
        return null;
    // Same reason as `n` above: an omitted `exportProviders` must degrade to the
    // generic "other tools" wording, never throw.
    const list = Array.isArray(providers) ? providers : [];
    const only = list.length === 1 ? list[0] : null;
    const where = only !== null && isExportProvider(only) ? getExportProviderLabel(only) : 'other tools';
    return count === 1
        ? `1 is already in ${where} and stays there`
        : `${count} are already in ${where} and stay there`;
}
function issueLines(impact) {
    const issues = n(impact.deletes, 'issues');
    const comments = n(impact.deletes, 'comments');
    const shares = n(impact.deletes, 'shareLinks');
    const out = [];
    if (issues > 0) {
        out.push(comments > 0
            ? `${plural(issues, 'issue', 'issues')} and ${issues === 1 ? 'its' : 'their'} ${plural(comments, 'comment', 'comments')}`
            : plural(issues, 'issue', 'issues'));
    }
    if (shares > 0)
        out.push(`Removed from ${plural(shares, 'share link', 'share links')}`);
    return out;
}
/**
 * Shared by the project confirm and the account confirm so the two cannot
 * drift: a sole-owned team's projects cascade the same `client_reviews` and
 * `client_comments` rows a single project delete does, and reading the same
 * loss in two different sentences on two confirms is how a user learns to
 * distrust both.
 */
function clientReviewLinkLine(links, clientComments) {
    if (links === 0)
        return null;
    const linkText = plural(links, 'client review link', 'client review links');
    if (clientComments === 0)
        return linkText;
    return clientComments === 1
        ? `${linkText} and the comment a client left`
        : `${linkText} and the ${clientComments} comments clients left`;
}
function projectLines(impact) {
    const d = impact.deletes;
    const out = [];
    const issues = n(d, 'issues');
    const comments = n(d, 'comments');
    if (issues > 0) {
        out.push(comments > 0
            ? `${plural(issues, 'issue', 'issues')} and ${plural(comments, 'comment', 'comments')}`
            : plural(issues, 'issue', 'issues'));
    }
    const linkLine = clientReviewLinkLine(n(d, 'clientReviewLinks'), n(d, 'clientComments'));
    if (linkLine)
        out.push(linkLine);
    const scans = n(d, 'scheduledScans');
    if (scans > 0)
        out.push(plural(scans, 'scheduled scan', 'scheduled scans'));
    const shares = n(d, 'shareLinks');
    if (shares > 0)
        out.push(plural(shares, 'share link', 'share links'));
    const figma = n(d, 'figmaFiles');
    if (figma > 0)
        out.push(plural(figma, 'Figma file link', 'Figma file links'));
    // Saved designs are user-owned copies (project_design_references); the
    // cascade takes them with the project, so the confirm names them.
    const designs = n(d, 'savedDesigns');
    if (designs > 0)
        out.push(plural(designs, 'saved design', 'saved designs'));
    return out;
}
function clientReviewLines(impact) {
    const comments = n(impact.deletes, 'clientComments');
    const lines = ['Clients lose access to it'];
    if (comments === 1)
        lines.push('The comment a client left is deleted');
    if (comments > 1)
        lines.push(`The ${comments} comments clients left are deleted`);
    const kept = n(impact.keeps, 'issuesFromClientComments');
    const keepLines = [];
    if (kept === 1)
        keepLines.push(comments === 1 ? 'The issue created from that comment stays' : 'The issue created from those comments stays');
    if (kept > 1)
        keepLines.push(`${kept} issues created from those comments stay`);
    return { lines, keepLines };
}
function accountLines(impact) {
    const lines = ['Your account'];
    const projects = n(impact.deletes, 'projects');
    const issues = n(impact.deletes, 'issues');
    if (projects > 0) {
        const p = plural(projects, 'project only you use', 'projects only you use');
        lines.push(issues > 0 ? `${p}, with ${plural(issues, 'issue', 'issues')}` : p);
    }
    // R34: a sole-owned team's projects cascade the same client review links,
    // client comments and share links a single project delete does, and until
    // fix round 2 this listed neither. Every one of them is a channel where
    // someone OUTSIDE the account loses access to something, which is exactly
    // what the lead "This permanently deletes:" promises to enumerate. Phrased
    // through the same helper the project confirm uses.
    const linkLine = clientReviewLinkLine(n(impact.deletes, 'clientReviewLinks'), n(impact.deletes, 'clientComments'));
    if (linkLine)
        lines.push(linkLine);
    const shares = n(impact.deletes, 'shareLinks');
    if (shares > 0)
        lines.push(plural(shares, 'share link', 'share links'));
    const designs = n(impact.deletes, 'savedDesigns');
    if (designs > 0)
        lines.push(plural(designs, 'saved design', 'saved designs'));
    const teams = n(impact.keeps, 'transferredTeams');
    const keptIssues = n(impact.keeps, 'transferredIssues');
    const keepLines = [];
    if (teams === 1 && impact.transferredTeamName && impact.successorName) {
        keepLines.push(keptIssues > 0
            ? `"${impact.transferredTeamName}" passes to ${impact.successorName} and keeps its ${plural(keptIssues, 'issue', 'issues')}`
            : `"${impact.transferredTeamName}" passes to ${impact.successorName}`);
    }
    else if (teams > 0) {
        const t = teams === 1 ? '1 team you share passes to a teammate' : `${teams} teams you share pass to a teammate`;
        keepLines.push(keptIssues > 0
            ? `${t} and ${teams === 1 ? 'keeps its' : 'keep their'} ${plural(keptIssues, 'issue', 'issues')}`
            : t);
    }
    return { lines, keepLines };
}
export function describeDeletionImpact(impact) {
    const count = impact.targetCount;
    // One source for the label, so the string a confirm shows WHILE COUNTING
    // (deletionConfirmLabel, from the caller's own count) is the same string it
    // shows once ready, and the danger button cannot resize under the cursor.
    const confirmLabel = deletionConfirmLabel(impact.kind, count);
    switch (impact.kind) {
        case 'issues': {
            const lines = issueLines(impact);
            // With only an issue count, the confirmation is one sentence on every
            // surface. Keep the itemized disclosure when comments or share links
            // add consequences (issueLines above owns those additional counts).
            const simple = n(impact.deletes, 'issues') > 0
                && n(impact.deletes, 'comments') === 0
                && n(impact.deletes, 'shareLinks') === 0;
            return {
                title: count === 1
                    ? (impact.targetName ? `Delete "${impact.targetName}"?` : 'Delete this issue?')
                    : `Delete ${count} issues?`,
                lead: simple ? `Permanently delete ${lines[0]}?` : leadOverLines(lines),
                lines: simple ? [] : lines,
                keepLines: [exportedLine(n(impact.keeps, 'exportedIssues'), impact.exportProviders)].filter((l) => l !== null),
                confirmLabel,
                typedPhrase: null,
            };
        }
        case 'projects': {
            const lines = projectLines(impact);
            return {
                title: count === 1
                    ? (impact.targetName ? `Delete "${impact.targetName}"?` : 'Delete this project?')
                    : `Delete ${count} projects?`,
                // An empty project is the one case where the count RAN and found
                // nothing, so it can say so rather than falling back to the bare
                // permanence line every other empty list gets.
                lead: lines.length > 0
                    ? LEAD_DELETES
                    : `We found nothing in ${count === 1 ? 'it' : 'them'} that a delete would remove. ${LEAD_UNDONE}`,
                lines,
                keepLines: [exportedLine(n(impact.keeps, 'exportedIssues'), impact.exportProviders)].filter((l) => l !== null),
                confirmLabel,
                typedPhrase: impact.othersLose
                    ? (count === 1 ? (impact.targetName ?? 'delete') : `delete ${count} projects`)
                    : null,
            };
        }
        case 'clientReview': {
            const { lines, keepLines } = clientReviewLines(impact);
            return {
                title: 'Delete this review link?',
                // Never LEAD_DELETES: these lines are CONSEQUENCES ("Clients lose
                // access to it"), not a list of things being deleted, so a colon
                // promising a delete list would misdescribe them.
                lead: LEAD_UNDONE,
                lines,
                keepLines,
                confirmLabel,
                typedPhrase: impact.othersLose ? 'delete' : null,
            };
        }
        case 'account': {
            const { lines, keepLines } = accountLines(impact);
            return {
                title: 'Delete your account?',
                lead: leadOverLines(lines),
                lines,
                keepLines,
                confirmLabel,
                typedPhrase: ACCOUNT_DELETE_CONFIRMATION_PHRASE,
            };
        }
        default: {
            // COMPILE TIME: adding a fifth ImpactKind without a case above makes
            // `impact.kind` non-never here and fails `typecheck`. RUNTIME: an
            // unrecognised kind still gets valid copy, because only team review
            // validates its seam (review-app/lib/deletionImpact.ts) while the
            // extension and dashboard cast the body — and this builder runs inside
            // render, so returning `undefined` threw on `.confirmLabel` and unmounted
            // the dialog at the moment the user pressed delete (incident 84d417a2).
            // Both, not one: the type error is for us, the copy is for the user.
            void impact.kind;
            return {
                // Names nothing and counts nothing: the payload carries counts, but we
                // do not know what they describe, so rendering them would be a guess
                // shown to someone about to delete. The lead says that plainly rather
                // than letting an empty list read as "this removes nothing".
                title: 'Delete this?',
                lead: 'We could not count what this removes.',
                lines: [],
                keepLines: [],
                confirmLabel,
                // Keeps the gate the callers already had: ProjectSettings and
                // GlobalProjectGroup both read `readyCopy ? readyCopy.typedPhrase :
                // 'delete'`, so a null here would REMOVE their typed confirmation for
                // exactly the payload we understand least.
                typedPhrase: 'delete',
            };
        }
    }
}
/** Typed-confirmation comparison, shared so every surface accepts the same input. */
export function typedPhraseMatches(typed, phrase) {
    return typed.trim().toLowerCase() === phrase.trim().toLowerCase();
}
/**
 * Derived from `IMPACT_KINDS`, not hand-typed: a second copy of that list is
 * how a seam ends up refusing a kind the builder handles.
 */
const KNOWN_KINDS = new Set(IMPACT_KINDS);
/**
 * `deletes` / `keeps` hold ImpactLines, and the ARRAY being an array says
 * nothing about what is in it: `Array.isArray([null])` is true, which is how
 * `deletes: [null]` passed the pre-move guard and threw
 * `Cannot read properties of null (reading 'key')` inside render. Checks the
 * two fields the builder reads (`n()` above) and no more, so a server that
 * adds a field to a line, or a line key we do not know yet, is not refused.
 */
const isImpactLine = (l) => typeof l === 'object' && l !== null &&
    typeof l.key === 'string' &&
    typeof l.count === 'number';
/**
 * Refuse a response body that is not a DeletionImpact, at the seam that
 * received it. THE one validator: all three surfaces (dashboard, extension,
 * team review) call this, so what counts as an acceptable payload cannot drift
 * between them, and a refusal degrades everywhere to the same place every
 * other failure of this request degrades to — the caller's own fallback copy,
 * which names the real target ("Delete 3 issues?").
 *
 * Two layers, and it matters which holds what. THIS rejects a body that is not
 * shaped like a DeletionImpact. `describeDeletionImpact` is TOTAL (a `default`
 * branch for an unrecognised kind, null-safe line reads) and is what makes a
 * crash impossible. A seam guard cannot be the only line, and a total builder
 * is not a reason to let a malformed payload through: without this the builder
 * answers with its GENERIC copy ("Delete this?"), which discloses less than
 * the caller's fallback did before the preview existed.
 *
 * Checks only what the builder actually READS, so a server that ADDS a field
 * is not refused: the kind it switches on, the target count, and the three
 * collections its line builders iterate (elements included). Deliberately NOT
 * checked: `targetName`, `transferredTeamName`, `successorName`, `othersLose`
 * — every read of those is a truthiness test or a template interpolation, so
 * an omitted one degrades to the unnamed wording rather than throwing, and
 * refusing the whole payload over one would lose counts we could have shown.
 *
 * `expected` is the kind the caller ASKED for, and passing it is how a seam
 * refuses a well-formed answer to a different question. Without it this checks
 * only that `kind` is A known kind, never that it is THE requested one, and
 * executed against the pre-fix guard an issues-shaped body answering an
 * account request validated:
 *
 *   isDeletionImpact({ kind: 'issues', ... })                      -> true
 *   describeDeletionImpact(that).typedPhrase                       -> null
 *
 * `account` is the only kind whose `typedPhrase` is unconditional, so that one
 * swap silently turns the hard "Delete account" typed gate into a single click.
 * Not reachable today (the server assembles the kind it was asked for, and the
 * surfaces key their queries per kind), which is exactly what makes it a
 * missing defence: nothing at the seam would notice if either fact changed.
 *
 * OPTIONAL so a seam that has not adopted it still validates shape rather than
 * silently accepting everything. Every caller should pass its `request.kind`:
 * `review-app/lib/deletionImpact.ts` (has `request` in scope at the call site),
 * the dashboard's `useDeletionImpact`, and the extension's `useDeletionImpact`.
 */
export function isDeletionImpact(value, expected) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return false;
    const v = value;
    return (typeof v.kind === 'string' &&
        KNOWN_KINDS.has(v.kind) &&
        (expected === undefined || v.kind === expected) &&
        typeof v.targetCount === 'number' &&
        Array.isArray(v.deletes) && v.deletes.every(isImpactLine) &&
        Array.isArray(v.keeps) && v.keeps.every(isImpactLine) &&
        Array.isArray(v.exportProviders) && v.exportProviders.every((p) => typeof p === 'string'));
}
//# sourceMappingURL=deletionImpact.js.map